import { useEffect, useState } from 'react';
import { PROSPECTOR_ENGINE_URL } from '../lib/engineUrls';

export function NotionIntakeRules() {
  const [rulesText, setRulesText] = useState('');
  const [status, setStatus] = useState<{ type: 'idle' | 'loading' | 'error' | 'success'; message?: string }>({
    type: 'idle'
  });
  const [meta, setMeta] = useState<{ source?: string; lastSyncedAt?: number; error?: string | null }>({});

  const loadRules = async () => {
    setStatus({ type: 'loading', message: 'Loading rules...' });
    try {
      const response = await fetch(`${PROSPECTOR_ENGINE_URL}/api/notion-intake/rules`);
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to load rules');
      }
      setRulesText(JSON.stringify(data.rules, null, 2));
      setMeta({
        source: data.source,
        lastSyncedAt: data.lastSyncedAt,
        error: data.error || null
      });
      setStatus({ type: 'success', message: 'Rules loaded.' });
    } catch (error) {
      setStatus({ type: 'error', message: error instanceof Error ? error.message : 'Failed to load rules' });
    }
  };

  const syncRules = async () => {
    setStatus({ type: 'loading', message: 'Syncing from Notion...' });
    try {
      const response = await fetch(`${PROSPECTOR_ENGINE_URL}/api/notion-intake/sync`, {
        method: 'POST'
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to sync rules');
      }
      setRulesText(JSON.stringify(data.rules, null, 2));
      setMeta({
        source: data.source,
        lastSyncedAt: data.lastSyncedAt,
        error: data.error || null
      });
      setStatus({ type: 'success', message: 'Rules synced.' });
    } catch (error) {
      setStatus({ type: 'error', message: error instanceof Error ? error.message : 'Failed to sync rules' });
    }
  };

  useEffect(() => {
    loadRules();
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-[var(--text-pri)] px-6 py-10">
      <div className="max-w-5xl mx-auto space-y-6">
        <header className="space-y-2">
          <h1 className="text-3xl font-semibold">Notion Intake Rules</h1>
          <p className="text-[var(--text-ter)]">
            Edit the JSON rule set that controls database selection and tagging.
          </p>
        </header>

        <div className="flex flex-wrap gap-3">
          <button
            className="px-4 py-2 rounded bg-[var(--bg-raised)] hover:bg-[var(--bg-raised)] transition"
            onClick={loadRules}
            type="button"
          >
            Reload
          </button>
          <button
            className="px-4 py-2 rounded bg-[var(--slate)] hover:bg-[var(--slate)] transition"
            onClick={syncRules}
            type="button"
          >
            Sync from Notion
          </button>
          {status.type !== 'idle' && (
            <div
              className={`px-3 py-2 rounded text-sm ${
                status.type === 'error' ? 'bg-[var(--coral)] text-[var(--coral)]' : 'bg-[var(--green)] text-[var(--green)] dark:text-[var(--text-ter)]'
              }`}
            >
              {status.message}
            </div>
          )}
        </div>

        {meta.source && (
          <div className="text-sm text-[var(--text-ter)]">
            Source: {meta.source} {meta.lastSyncedAt ? `• Last synced ${new Date(meta.lastSyncedAt).toLocaleString()}` : ''}
            {meta.error ? ` • Last error: ${meta.error}` : ''}
          </div>
        )}

        <textarea
          className="w-full min-h-[520px] rounded border border-[var(--rule)] bg-[var(--bg)] text-[var(--text-pri)] p-4 font-mono text-sm"
          value={rulesText}
          readOnly
        />
      </div>
    </div>
  );
}

